import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { get, post, ApiError } from './api.ts';
import type { Role } from './types.ts';

interface AuthState {
  role: Role | null;
  /** True until the first /auth/me has resolved, so we never flash the login screen. */
  loading: boolean;
  signIn: (password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [role, setRole] = useState<Role | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    get<{ role: Role }>('/auth/me')
      .then((result) => {
        if (!cancelled) setRole(result.role);
      })
      .catch(() => {
        // A 401 here is the normal signed-out case, not an error worth showing.
        if (!cancelled) setRole(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async (password: string) => {
    const result = await post<{ role: Role }>('/auth/login', { password });
    setRole(result.role);
  }, []);

  const signOut = useCallback(async () => {
    await post('/auth/logout', {});
    setRole(null);
  }, []);

  return (
    <AuthContext.Provider value={{ role, loading, signIn, signOut }}>{children}</AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}

/** Guests get a read-only view; every mutating control checks this. */
export function useCanWrite(): boolean {
  return useAuth().role === 'admin';
}

export { ApiError };
